import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const RATE_LIMIT = 2; // max messages
const RATE_LIMIT_HOURS = 24; // timeframe in hours

function MessagesPage({ userDetails }) {
  const [tickets, setTickets] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [messagesSentToday, setMessagesSentToday] = useState(0);
  const [firstMessageTimestamp, setFirstMessageTimestamp] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const username = userDetails?.username || '';
  const email = userDetails?.email || '';

  // Storage keys only use username now
  const storageKeyCount = useMemo(() => `messagesSentToday_${username}`, [username]);
  const storageKeyTimestamp = useMemo(() => `firstMessageTimestamp_${username}`, [username]);

  // Fetch tickets and load rate limit info on username change
  useEffect(() => {
    if (!username) return;

    // Fetch tickets
    axios
      .get(`https://www.sharedvision-partners.online/v1/API/Admin/user.php?user=${encodeURIComponent(username)}`)
      .then((res) => setTickets(res.data))
      .catch(console.error);

    // Load rate limit info from localStorage
    const storedCount = localStorage.getItem(storageKeyCount);
    const storedFirst = localStorage.getItem(storageKeyTimestamp);

    if (storedCount) setMessagesSentToday(Number(storedCount));
    if (storedFirst) setFirstMessageTimestamp(new Date(storedFirst));
  }, [username, storageKeyCount, storageKeyTimestamp]);

  // Check and reset rate limit every 24 hrs
  useEffect(() => {
    if (!firstMessageTimestamp) return;

    const now = new Date();
    const diffHours = (now - new Date(firstMessageTimestamp)) / 1000 / 3600;

    if (diffHours > RATE_LIMIT_HOURS) {
      setMessagesSentToday(0);
      setFirstMessageTimestamp(null);
      localStorage.removeItem(storageKeyCount);
      localStorage.removeItem(storageKeyTimestamp);
    }
  }, [firstMessageTimestamp, storageKeyCount, storageKeyTimestamp]);

  const handleSubmit = async () => {
    setError('');

    if (messagesSentToday >= RATE_LIMIT) {
      setError(`You have reached the limit of ${RATE_LIMIT} messages in ${RATE_LIMIT_HOURS} hours.`);
      return;
    }

    if (!subject.trim() || !message.trim()) {
      setError('Subject and message cannot be empty.');
      return;
    }

    setLoading(true);
    try {
      await axios.post('https://www.sharedvision-partners.online/v1/API/Admin/create.php', {
        username,
        email,
        subject,
        message,
      });

      const now = new Date();
      if (!firstMessageTimestamp) {
        setFirstMessageTimestamp(now);
        localStorage.setItem(storageKeyTimestamp, now.toISOString());
      }
      setMessagesSentToday((prev) => {
        const newCount = prev + 1;
        localStorage.setItem(storageKeyCount, newCount);
        return newCount;
      });

      setSubject('');
      setMessage('');
      setError('');

      const res = await axios.get(`https://www.sharedvision-partners.online/v1/API/Admin/user.php?user=${encodeURIComponent(username)}`);
      setTickets(res.data);
    } catch {
      setError('Failed to send ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isRateLimited = messagesSentToday >= RATE_LIMIT;

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Support Tickets</h1>
          {username && <p style={styles.userInfo}><strong>User:</strong> {username}</p>}
          {email && <p style={styles.userInfo}><strong>Email:</strong> {email}</p>}
        </div>
      </header>

      <section style={styles.formSection}>
        <h2 style={styles.sectionTitle}>Submit a Ticket</h2>
        {error && <p style={styles.error}>{error}</p>}
        <label style={styles.label} htmlFor="subject">Subject</label>
        <input
          id="subject"
          type="text"
          placeholder="Enter subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          style={styles.input}
          disabled={loading || isRateLimited}
        />
        <label style={styles.label} htmlFor="message">Message</label>
        <textarea
          id="message"
          placeholder="Write your message here..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          style={styles.textarea}
          disabled={loading || isRateLimited}
        />
        <button
          onClick={handleSubmit}
          disabled={loading || isRateLimited}
          style={{ ...styles.button, ...(loading || isRateLimited ? styles.buttonDisabled : {}) }}
        >
          {loading ? 'Sending...' : isRateLimited ? `Limit Reached (${RATE_LIMIT} per ${RATE_LIMIT_HOURS} hrs)` : 'Send Ticket'}
        </button>
      </section>

      <section style={styles.ticketSection}>
        <h2 style={styles.sectionTitle}>Your Tickets</h2>
        {tickets.length === 0 ? (
          <p style={styles.noTickets}>No tickets submitted yet.</p>
        ) : (
          <ul style={styles.ticketList}>
            {tickets.map((t) => (
              <li key={t.id} style={styles.ticketItem}>
                <div style={styles.ticketHeader}>
                  <strong>{t.subject}</strong>
                  <span
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusColor(t.status),
                    }}
                  >
                    {t.status}
                  </span>
                </div>
                {t.message && <p style={styles.ticketMessage}>{t.message}</p>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// Helper for status colors
function getStatusColor(status) {
  switch (status?.toLowerCase()) {
    case 'open':
      return '#2ecc71'; // green
    case 'pending':
      return '#f1c40f'; // yellow
    case 'closed':
      return '#e74c3c'; // red
    default:
      return '#95a5a6'; // grey
  }
}

const styles = {
  container: {
    maxWidth: 600,
    margin: 'auto',
    padding: 20,
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    color: '#333',
  },
  header: {
    marginBottom: 20,
    borderBottom: '2px solid #eee',
    paddingBottom: 10,
  },
  title: {
    margin: 0,
    fontSize: '1.8rem',
    color: '#2c3e50',
  },
  userInfo: {
    margin: '4px 0',
    fontSize: '0.9rem',
    color: '#555',
  },
  formSection: {
    marginBottom: 40,
  },
  sectionTitle: {
    fontSize: '1.5rem',
    marginBottom: 15,
    color: '#34495e',
  },
  error: {
    color: '#e74c3c',
    marginBottom: 12,
  },
  label: {
    display: 'block',
    marginBottom: 6,
    fontWeight: '600',
  },
  input: {
    width: '100%',
    padding: 10,
    fontSize: '1rem',
    borderRadius: 4,
    border: '1px solid #ccc',
    marginBottom: 15,
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: 10,
    fontSize: '1rem',
    borderRadius: 4,
    border: '1px solid #ccc',
    marginBottom: 20,
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  button: {
    padding: '12px 20px',
    fontSize: '1rem',
    borderRadius: 4,
    border: 'none',
    backgroundColor: '#2980b9',
    color: 'white',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
  },
  buttonDisabled: {
    backgroundColor: '#95a5a6',
    cursor: 'not-allowed',
  },
  ticketSection: {
    marginBottom: 20,
  },
  noTickets: {
    fontStyle: 'italic',
    color: '#777',
  },
  ticketList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    maxHeight: 300,
    overflowY: 'auto',
    borderTop: '1px solid #ddd',
  },
  ticketItem: {
    padding: '12px 0',
    borderBottom: '1px solid #ddd',
  },
  ticketHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: 12,
    fontSize: '0.75rem',
    color: 'white',
    textTransform: 'capitalize',
  },
  ticketMessage: {
    margin: 0,
    color: '#555',
    fontSize: '0.9rem',
  },
};

export default MessagesPage;
